import _slicedToArray from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _defineProperty from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _objectWithoutProperties from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["autoAlign", "className", "decorator", "disabled", "direction", "items", "label", "aria-label", "ariaLabel", "itemToString", "itemToElement", "renderSelectedItem", "type", "size", "onChange", "id", "titleText", "hideLabel", "helperText", "translateWithId", "light", "invalid", "invalidText", "warn", "warnText", "initialSelectedItem", "selectedItem", "downshiftProps", "readOnly", "slug"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/**
 * Copyright IBM Corp. 2016, 2023
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useEffect, useContext, useCallback, useMemo, useState, isValidElement, cloneElement } from 'react';
import { useSelect } from 'downshift';
import cx from 'classnames';
import { WarningFilled, WarningAltFilled, Checkmark } from '@carbon/icons-react';
import ListBox from '../ListBox/index.js';
import mergeRefs from '../../tools/mergeRefs.js';
import { deprecate } from '../../prop-types/deprecate.js';
import { usePrefix } from '../../internal/usePrefix.js';
import '../FluidForm/FluidForm.js';
import { FormContext } from '../FluidForm/FormContext.js';
import { useId } from '../../internal/useId.js';
import { useFloating, autoUpdate, size, flip, hide } from '@floating-ui/react';
import { useFeatureFlag } from '../FeatureFlags/index.js';
import { AILabel } from '../AILabel/index.js';
import { isComponentElement } from '../../internal/utils.js';
import { ListBoxTypePropType, ListBoxSizePropType } from '../ListBox/ListBoxPropTypes.js';
var _useSelect$stateChang = useSelect.stateChangeTypes,
  ItemMouseMove = _useSelect$stateChang.ItemMouseMove,
  MenuMouseLeave = _useSelect$stateChang.MenuMouseLeave;
var defaultItemToString = function defaultItemToString(item) {
  if (typeof item === 'string') {
    return item;
  }
  if (typeof item === 'number') {
    return "".concat(item);
  }
  if (item !== null && typeof item === 'object' && 'label' in item && typeof item['label'] === 'string') {
    return item['label'];
  }
  return '';
};
/**
 * Custom state reducer for `useSelect` in Downshift, providing control over
 * state changes.
 *
 * This function is called each time `useSelect` updates its internal state or
 * triggers `onStateChange`. It allows for fine-grained control of state
 * updates by modifying or overriding the default changes from Downshift's
 * reducer.
 * https://github.com/downshift-js/downshift/tree/master/src/hooks/useSelect#statereducer
 *
 * @param {Object} state - The current full state of the Downshift component.
 * @param {Object} actionAndChanges - Contains the action type and proposed
 * changes from the default Downshift reducer.
 * @param {Object} actionAndChanges.changes - Suggested state changes.
 * @param {string} actionAndChanges.type - The action type for the state
 * change (e.g., item selection).
 * @returns {Object} - The modified state based on custom logic or default
 * changes if no custom logic applies.
 */
function stateReducer(state, actionAndChanges) {
  var changes = actionAndChanges.changes,
    type = actionAndChanges.type;
  switch (type) {
    case ItemMouseMove:
    case MenuMouseLeave:
      if (changes.highlightedIndex === state.highlightedIndex) {
        // Prevent state update if highlightedIndex hasn't changed
        return state;
      }
      return changes;
    default:
      return changes;
  }
}
var Dropdown = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var _cx, _cx2, _cx4;
  var _ref$autoAlign = _ref.autoAlign,
    autoAlign = _ref$autoAlign === void 0 ? false : _ref$autoAlign,
    containerClassName = _ref.className,
    decorator = _ref.decorator,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === void 0 ? false : _ref$disabled,
    _ref$direction = _ref.direction,
    direction = _ref$direction === void 0 ? 'bottom' : _ref$direction,
    itemsProp = _ref.items,
    label = _ref.label,
    ariaLabel = _ref['aria-label'],
    deprecatedAriaLabel = _ref.ariaLabel,
    _ref$itemToString = _ref.itemToString,
    itemToString = _ref$itemToString === void 0 ? defaultItemToString : _ref$itemToString,
    _ref$itemToElement = _ref.itemToElement,
    itemToElement = _ref$itemToElement === void 0 ? null : _ref$itemToElement,
    renderSelectedItem = _ref.renderSelectedItem,
    _ref$type = _ref.type,
    type = _ref$type === void 0 ? 'default' : _ref$type,
    size$1 = _ref.size,
    onChange = _ref.onChange,
    id = _ref.id,
    _ref$titleText = _ref.titleText,
    titleText = _ref$titleText === void 0 ? '' : _ref$titleText,
    hideLabel = _ref.hideLabel,
    _ref$helperText = _ref.helperText,
    helperText = _ref$helperText === void 0 ? '' : _ref$helperText,
    translateWithId = _ref.translateWithId,
    light = _ref.light,
    invalid = _ref.invalid,
    invalidText = _ref.invalidText,
    warn = _ref.warn,
    warnText = _ref.warnText,
    initialSelectedItem = _ref.initialSelectedItem,
    controlledSelectedItem = _ref.selectedItem,
    downshiftProps = _ref.downshiftProps,
    readOnly = _ref.readOnly,
    slug = _ref.slug,
    other = _objectWithoutProperties(_ref, _excluded);
  var enableFloatingStyles = useFeatureFlag('enable-v12-dynamic-floating-styles');
  var _useFloating = useFloating(enableFloatingStyles || autoAlign ? {
      placement: direction,
      // The floating element is positioned relative to its nearest
      // containing block (usually the viewport). It will in many cases also
      // “break” the floating element out of a clipping ancestor.
      // https://floating-ui.com/docs/misc#clipping
      strategy: 'fixed',
      // Middleware order matters, arrow should be last
      middleware: [size({
        apply: function apply(_ref2) {
          var rects = _ref2.rects,
            elements = _ref2.elements;
          Object.assign(elements.floating.style, {
            width: "".concat(rects.reference.width, "px")
          });
        }
      }), autoAlign && flip(), autoAlign && hide()],
      whileElementsMounted: autoUpdate
    } : {}
    // When autoAlign is turned off & the `enable-v12-dynamic-floating-styles` feature flag is not
    // enabled, floating-ui will not be used
    ),
    refs = _useFloating.refs,
    floatingStyles = _useFloating.floatingStyles,
    middlewareData = _useFloating.middlewareData;
  useEffect(function () {
    if (enableFloatingStyles || autoAlign) {
      var _middlewareData$hide;
      var updatedFloatingStyles = _objectSpread(_objectSpread({}, floatingStyles), {}, {
        visibility: (_middlewareData$hide = middlewareData.hide) !== null && _middlewareData$hide !== void 0 && _middlewareData$hide.referenceHidden ? 'hidden' : 'visible'
      });
      Object.keys(updatedFloatingStyles).forEach(function (style) {
        if (refs.floating.current) {
          refs.floating.current.style[style] = updatedFloatingStyles[style];
        }
      });
    }
  }, [floatingStyles, autoAlign, refs.floating]);
  var prefix = usePrefix();
  var _useContext = useContext(FormContext),
    isFluid = _useContext.isFluid;
  var onSelectedItemChange = useCallback(function (_ref3) {
    var selectedItem = _ref3.selectedItem;
    if (onChange) {
      onChange({
        selectedItem: selectedItem !== null && selectedItem !== void 0 ? selectedItem : null
      });
    }
  }, [onChange]);
  var isItemDisabled = useCallback(function (item, _index) {
    var isObject = item !== null && typeof item === 'object';
    return isObject && 'disabled' in item && item.disabled === true;
  }, []);
  var onHighlightedIndexChange = useCallback(function (changes) {
    var highlightedIndex = changes.highlightedIndex;
    if (highlightedIndex !== undefined && highlightedIndex > -1 && typeof window !== undefined) {
      var itemArray = document.querySelectorAll("li.".concat(prefix, "--list-box__menu-item[role=\"option\"]"));
      var highlightedItem = itemArray[highlightedIndex];
      if (highlightedItem) {
        highlightedItem.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        });
      }
    }
  }, [prefix]);
  var items = useMemo(function () {
    return itemsProp;
  }, [itemsProp]);
  var selectProps = useMemo(function () {
    return _objectSpread({
      items: items,
      itemToString: itemToString,
      initialSelectedItem: initialSelectedItem,
      onSelectedItemChange: onSelectedItemChange,
      stateReducer: stateReducer,
      isItemDisabled: isItemDisabled,
      onHighlightedIndexChange: onHighlightedIndexChange
    }, downshiftProps);
  }, [items, itemToString, initialSelectedItem, onSelectedItemChange, stateReducer, isItemDisabled, onHighlightedIndexChange, downshiftProps]);
  var dropdownInstanceId = useId();

  // only set selectedItem if the prop is defined. Setting if it is undefined
  // will overwrite default selected items from useSelect
  if (controlledSelectedItem !== undefined) {
    selectProps.selectedItem = controlledSelectedItem;
  }
  var _useSelect = useSelect(selectProps),
    isOpen = _useSelect.isOpen,
    getToggleButtonProps = _useSelect.getToggleButtonProps,
    getLabelProps = _useSelect.getLabelProps,
    getMenuProps = _useSelect.getMenuProps,
    getItemProps = _useSelect.getItemProps,
    selectedItem = _useSelect.selectedItem,
    highlightedIndex = _useSelect.highlightedIndex;
  var inline = type === 'inline';
  var showWarning = !invalid && warn;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isFocused = _useState2[0],
    setIsFocused = _useState2[1];
  var className = cx("".concat(prefix, "--dropdown"), (_cx = {}, _defineProperty(_cx, "".concat(prefix, "--dropdown--invalid"), invalid), _defineProperty(_cx, "".concat(prefix, "--dropdown--warning"), showWarning), _defineProperty(_cx, "".concat(prefix, "--dropdown--open"), isOpen), _defineProperty(_cx, "".concat(prefix, "--dropdown--focus"), isFocused), _defineProperty(_cx, "".concat(prefix, "--dropdown--inline"), inline), _defineProperty(_cx, "".concat(prefix, "--dropdown--disabled"), disabled), _defineProperty(_cx, "".concat(prefix, "--dropdown--light"), light), _defineProperty(_cx, "".concat(prefix, "--dropdown--readonly"), readOnly), _defineProperty(_cx, "".concat(prefix, "--dropdown--").concat(size$1), size$1), _defineProperty(_cx, "".concat(prefix, "--list-box--up"), direction === 'top'), _defineProperty(_cx, "".concat(prefix, "--autoalign"), autoAlign), _cx));
  var titleClasses = cx("".concat(prefix, "--label"), (_cx2 = {}, _defineProperty(_cx2, "".concat(prefix, "--label--disabled"), disabled), _defineProperty(_cx2, "".concat(prefix, "--visually-hidden"), hideLabel), _cx2));
  var helperClasses = cx("".concat(prefix, "--form__helper-text"), _defineProperty({}, "".concat(prefix, "--form__helper-text--disabled"), disabled));
  var wrapperClasses = cx("".concat(prefix, "--dropdown__wrapper"), "".concat(prefix, "--list-box__wrapper"), containerClassName, (_cx4 = {}, _defineProperty(_cx4, "".concat(prefix, "--dropdown__wrapper--inline"), inline), _defineProperty(_cx4, "".concat(prefix, "--list-box__wrapper--inline"), inline), _defineProperty(_cx4, "".concat(prefix, "--dropdown__wrapper--inline--invalid"), inline && invalid), _defineProperty(_cx4, "".concat(prefix, "--list-box__wrapper--inline--invalid"), inline && invalid), _defineProperty(_cx4, "".concat(prefix, "--list-box__wrapper--fluid--invalid"), isFluid && invalid), _defineProperty(_cx4, "".concat(prefix, "--list-box__wrapper--slug"), slug), _defineProperty(_cx4, "".concat(prefix, "--list-box__wrapper--decorator"), decorator), _cx4));
  var helperId = !helperText ? undefined : "dropdown-helper-text-".concat(dropdownInstanceId);

  // needs to be Capitalized for react to render it correctly
  var ItemToElement = itemToElement;
  var toggleButtonProps = getToggleButtonProps({
    'aria-label': ariaLabel || deprecatedAriaLabel
  });
  var helper = helperText && !isFluid ? /*#__PURE__*/React.createElement("div", {
    id: helperId,
    className: helperClasses
  }, helperText) : null;
  var handleFocus = function handleFocus(evt) {
    setIsFocused(evt.type === 'focus' && !selectedItem ? true : false);
  };
  var mergedRef = mergeRefs(toggleButtonProps.ref, ref);
  var _useState3 = useState(),
    _useState4 = _slicedToArray(_useState3, 2),
    currTimer = _useState4[0],
    setCurrTimer = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    isTyping = _useState6[0],
    setIsTyping = _useState6[1];
  var onKeyDownHandler = useCallback(function (evt) {
    if (evt.code !== 'Space' || !['ArrowDown', 'ArrowUp', ' ', 'Enter'].includes(evt.key)) {
      setIsTyping(true);
    }
    if (isTyping && evt.code === 'Space' || !['ArrowDown', 'ArrowUp', ' ', 'Enter'].includes(evt.key)) {
      if (currTimer) {
        clearTimeout(currTimer);
      }
      setCurrTimer(setTimeout(function () {
        setIsTyping(false);
      }, 3000));
    }
    if (['ArrowDown'].includes(evt.key)) {
      setIsFocused(false);
    }
    if (['Enter'].includes(evt.key) && !selectedItem && !isOpen) {
      setIsFocused(true);
    }

    // For Dropdowns the arrow up key is only allowed if the Dropdown is open
    if (toggleButtonProps.onKeyDown && (evt.key !== 'ArrowUp' || isOpen && evt.key === 'ArrowUp')) {
      toggleButtonProps.onKeyDown(evt);
    }
  }, [isTyping, currTimer, toggleButtonProps]);
  var readOnlyEventHandlers = useMemo(function () {
    if (readOnly) {
      return {
        onClick: function onClick(evt) {
          var _mergedRef$current;
          // NOTE: does not prevent click
          evt.preventDefault();
          // focus on the element as per readonly input behavior
          mergedRef === null || mergedRef === void 0 || (_mergedRef$current = mergedRef.current) === null || _mergedRef$current === void 0 ? void 0 : _mergedRef$current.focus();
        },
        onKeyDown: function onKeyDown(evt) {
          var selectAccessKeys = ['ArrowDown', 'ArrowUp', ' ', 'Enter'];
          // This prevents the select from opening for the above keys
          if (selectAccessKeys.includes(evt.key)) {
            evt.preventDefault();
          }
        }
      };
    } else {
      return {
        onKeyDown: onKeyDownHandler
      };
    }
  }, [readOnly, onKeyDownHandler]);
  var menuProps = useMemo(function () {
    return getMenuProps({
      ref: enableFloatingStyles || autoAlign ? refs.setFloating : null
    });
  }, [autoAlign, getMenuProps, refs.setFloating, enableFloatingStyles]);

  // AILabel is always size `mini`
  var candidate = slug !== null && slug !== void 0 ? slug : decorator;
  var candidateIsAILabel = isComponentElement(candidate, AILabel);
  var normalizedDecorator = candidateIsAILabel ? /*#__PURE__*/cloneElement(candidate, {
    size: 'mini'
  }) : null;
  var allLabelProps = getLabelProps();
  var labelProps = /*#__PURE__*/isValidElement(titleText) ? {
    id: allLabelProps.id
  } : allLabelProps;
  return /*#__PURE__*/React.createElement("div", _extends({
    className: wrapperClasses
  }, other), titleText && /*#__PURE__*/React.createElement("label", _extends({
    className: titleClasses
  }, labelProps), titleText), /*#__PURE__*/React.createElement(ListBox, {
    onFocus: handleFocus,
    onBlur: handleFocus,
    size: size$1,
    className: className,
    invalid: invalid,
    invalidText: invalidText,
    warn: warn,
    warnText: warnText,
    light: light,
    isOpen: isOpen,
    ref: enableFloatingStyles || autoAlign ? refs.setReference : null,
    id: id
  }, invalid && /*#__PURE__*/React.createElement(WarningFilled, {
    className: "".concat(prefix, "--list-box__invalid-icon")
  }), showWarning && /*#__PURE__*/React.createElement(WarningAltFilled, {
    className: "".concat(prefix, "--list-box__invalid-icon ").concat(prefix, "--list-box__invalid-icon--warning")
  }), /*#__PURE__*/React.createElement("button", _extends({
    type: "button"
    // aria-expanded is already being passed through {...toggleButtonProps}
    ,

    className: "".concat(prefix, "--list-box__field"),
    disabled: disabled,
    "aria-disabled": readOnly ? true : undefined // aria-disabled to remain focusable
    ,

    "aria-describedby": !inline && !invalid && !warn && helper ? helperId : undefined,
    title: selectedItem && itemToString !== undefined ? itemToString(selectedItem) : defaultItemToString(label)
  }, toggleButtonProps, readOnlyEventHandlers, {
    ref: mergedRef
  }), /*#__PURE__*/React.createElement("span", {
    className: "".concat(prefix, "--list-box__label")
  }, selectedItem ? renderSelectedItem ? renderSelectedItem(selectedItem) : itemToString(selectedItem) : label), /*#__PURE__*/React.createElement(ListBox.MenuIcon, {
    isOpen: isOpen,
    translateWithId: translateWithId
  })), slug ? normalizedDecorator : decorator ? /*#__PURE__*/React.createElement("div", {
    className: "".concat(prefix, "--list-box__inner-wrapper--decorator")
  }, normalizedDecorator) : '', /*#__PURE__*/React.createElement(ListBox.Menu, menuProps, isOpen && items.map(function (item, index) {
    var isObject = item !== null && typeof item === 'object';
    var itemProps = getItemProps({
      item: item,
      index: index
    });
    var title = isObject && 'text' in item && itemToElement ? item.text : itemToString(item);
    return /*#__PURE__*/React.createElement(ListBox.MenuItem, _extends({
      key: itemProps.id,
      isActive: selectedItem === item,
      isHighlighted: highlightedIndex === index,
      title: title,
      disabled: itemProps['aria-disabled']
    }, itemProps), typeof item === 'object' && ItemToElement !== undefined && ItemToElement !== null ? /*#__PURE__*/React.createElement(ItemToElement, _extends({
      key: itemProps.id
    }, item)) : itemToString(item), selectedItem === item && /*#__PURE__*/React.createElement(Checkmark, {
      className: "".concat(prefix, "--list-box__menu-item__selected-icon")
    }));
  }))), !inline && !invalid && !warn && helper);
});

// Workaround problems with forwardRef() and generics.  In the long term, should stop using forwardRef().
// See https://stackoverflow.com/questions/58469229/react-with-typescript-generics-while-using-react-forwardref.

Dropdown.displayName = 'Dropdown';
export { Dropdown as default };